import { type FC, useState } from "react";
import { ChevronDown, Lock } from "lucide-react";

import { cn } from "@/shared/utils/core";
import { Button } from "@/shared/components/ui/button";
import { getActivityIcon, getActorLabel } from "@/shared/utils/activityLogDisplay";
import { formatDollars } from "@/shared/utils/formatters";
import { BALANCE_ESTIMATE_RANGES } from "@/shared/constants/balanceEstimates";
import { Skeleton } from "@/shared/components/ui/skeleton";
import type { ActionType, ActivityLogEntry } from "@/shared/types/activityLog";

const ACTION_LABELS: Partial<Record<ActionType, string>> = {
  added_estate_item: "added this item",
  updated_estate_item: "updated this item",
  approved_estate_item: "accepted parsed document",
  declined_estate_item: "denied parsed document",
  deleted_estate_item: "removed this item",
  uploaded_document: "attached a document",
  deleted_document: "removed a document",
  property_updated: "changed the address",
};

interface ActivityDisplay {
  label: string;
  subtitle?: string;
}

function getActivityDisplay(entry: ActivityLogEntry): ActivityDisplay {
  if (entry.action_type === "uploaded_document") {
    const name =
      (entry.metadata.display_name as string | undefined) ||
      (entry.metadata.file_name as string | undefined);
    return { label: name ? `attached ${name}` : "attached a document" };
  }

  if (entry.action_type === "updated_estate_item") {
    const before = entry.metadata.before as Record<string, unknown> | undefined;
    const after = entry.metadata.after as Record<string, unknown> | undefined;

    if (after?.notes && after.notes !== before?.notes) {
      const preview =
        typeof after.notes === "string" && after.notes.length > 80
          ? `${after.notes.slice(0, 80)}...`
          : (after.notes as string);
      return { label: "added a note", subtitle: `"${preview}"` };
    }

    if (
      typeof before?.balance === "number" &&
      typeof after?.balance === "number" &&
      before.balance !== after.balance
    ) {
      const old = formatDollars({ amount: before.balance, rounding: "whole" });
      const updated = formatDollars({ amount: after.balance, rounding: "whole" });
      return { label: `changed value (${old} → ${updated})` };
    }

    if (before && after) {
      const estimateChanged =
        before.is_estimated !== after.is_estimated ||
        before.balance_estimate_range !== after.balance_estimate_range ||
        before.value_label !== after.value_label;

      if (estimateChanged) {
        const rangeKey = after.balance_estimate_range as string | undefined;
        const rangeLabel = rangeKey
          ? BALANCE_ESTIMATE_RANGES.find((r) => r.key === rangeKey)?.label
          : undefined;
        return {
          label: rangeLabel ? `updated estimated value (${rangeLabel})` : "updated estimated value",
        };
      }

      const FIELD_LABELS: Record<string, string> = {
        item_name: "name",
        classification: "classification",
        item_type: "type",
        identifiers: "account details",
        status_flag: "status",
        ownership: "ownership",
      };
      const changed = Object.keys(FIELD_LABELS).filter(
        (key) => JSON.stringify(before[key]) !== JSON.stringify(after[key]),
      );
      if (changed.length > 0) {
        const fields = changed.map((k) => FIELD_LABELS[k]).join(", ");
        return { label: `updated ${fields}` };
      }
    }
  }

  const label = ACTION_LABELS[entry.action_type] ?? entry.action_type.replace(/_/g, " ");
  return { label };
}

function formatTimestamp(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

interface ActivityLogSectionProps {
  entries: ActivityLogEntry[];
  isLoading: boolean;
  isPaidUser?: boolean;
  personName?: string;
  onUpgrade?: () => void;
}

export const ActivityLogSection: FC<ActivityLogSectionProps> = ({
  entries,
  isLoading,
  isPaidUser = true,
  personName = "the estate",
  onUpgrade,
}) => {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? entries : entries.slice(0, 3);

  return (
    <div className="px-4 py-4 sm:px-6 sm:py-6">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-baseline gap-1.5">
          <h4>Activity log</h4>
          {entries.length > 0 && (
            <span className="text-sm text-secondary">
              {entries.length} {entries.length === 1 ? "event" : "events"}
            </span>
          )}
        </div>
        {entries.length > 3 && isPaidUser && (
          <button
            onClick={() => setShowAll((s) => !s)}
            className="flex items-center gap-1 text-sm text-brand transition-colors hover:text-brand/80"
          >
            {showAll ? "Show less" : "Show all"}
            <ChevronDown className={cn("h-4 w-4 transition-transform", showAll && "rotate-180")} />
          </button>
        )}
      </div>
      {isLoading ? (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-8 w-full rounded-lg" />
          <Skeleton className="h-8 w-full rounded-lg" />
          <Skeleton className="h-8 w-full rounded-lg" />
        </div>
      ) : entries.length === 0 ? (
        <p className="text-sm text-tertiary">No activity yet.</p>
      ) : (
        <div className={cn("relative", !isPaidUser && "min-h-[200px]")}>
          {isPaidUser ? (
            <div className="flex flex-col gap-4">
              {visible.map((entry) => {
                const { Icon: ActivityIcon, bg, fg } = getActivityIcon(entry);
                const actorLabel = getActorLabel(entry);
                const { label, subtitle } = getActivityDisplay(entry);
                return (
                  <div key={entry.id} className="flex items-start gap-3">
                    <div
                      className={cn(
                        "flex h-7 w-7 shrink-0 items-center justify-center rounded-full",
                        bg,
                      )}
                    >
                      <ActivityIcon className={cn("h-3.5 w-3.5", fg)} />
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col">
                      <div className="flex items-baseline justify-between gap-2">
                        <p className="text-sm">
                          <span className="font-medium">{actorLabel}</span>{" "}
                          <span className="text-secondary">{label}</span>
                        </p>
                        <span className="shrink-0 text-2xs text-tertiary">
                          {formatTimestamp(entry.created_at)}
                        </span>
                      </div>
                      {subtitle && (
                        <p className="mt-0.5 truncate text-sm text-tertiary">{subtitle}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col gap-4 select-none blur-sm" aria-hidden="true">
              {Array.from({ length: Math.min(entries.length, 3) }).map((_, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="h-7 w-7 shrink-0 rounded-full bg-subtle" />
                  <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <div className="h-4 w-3/4 rounded-sm bg-subtle" />
                    <div className="h-3 w-1/2 rounded-sm bg-subtle" />
                  </div>
                </div>
              ))}
            </div>
          )}
          {!isPaidUser && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="flex max-w-md flex-col items-center gap-2 rounded-lg border border-primary bg-white p-6 text-center shadow-sm">
                <Lock className="h-5 w-5 text-secondary" />
                <div>
                  <p className="text-sm font-medium">See every update to {personName}'s estate</p>
                  <p className="mt-1 text-sm text-secondary">
                    Upgrade to access the full history of changes across {personName}'s estate.
                  </p>
                </div>
                <Button variant="outline" size="md" onClick={onUpgrade}>
                  Learn more
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
